import { siteApi } from "@api";
import BrandBanner from "@components/data-display/BrandBanner";
import { APP_INSTALLER_HEIGHT, HEADER_HEIGHT, Paths } from "@constants";
import useAuthSession from "@hooks/useAuthSession";
import useTypedSelector from "@hooks/useTypedSelector";
import useDetectScroll from "@smakss/react-scroll-direction";
import { UserMode } from "@types";
import { GameListRef } from "@views/HomePage/components/GameList/GameList.tsx";
import { GameCategoryModel, GameProviderModel, cn, useStickyDetection } from "kz-ui-sdk";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdSearch } from "react-icons/md";
import { matchPath, useLocation } from "react-router-dom";
import {
  GameCategoryList,
  GameList,
  GameProviderList,
  GameSearchModal,
  MiniPromotionBanners,
  RecentGames,
} from "./components";
import { showGameSearchModal } from "./components/GameSearchModal/utils";
import { CATEGORY_POPULAR_SLUG, RECENT_GAME_QUERY_PARAMS } from "./constants";
import { useGameDataSource } from "./hooks";
import { standardizeGameStats } from "./utils";

const CATEGORY_CONTAINER_HEIGHT = 65;
const BANNER_HEIGHT = 100;
const MICRO_BANNER_HEIGHT = 52;
const BANNER_PADDING = 34;
const SCROLL_DOWN_OFFSET = 60;

const threshold = [1];

const HomePage = () => {
  const { userMode } = useAuthSession();
  const location = useLocation();
  const isDisplayAppInstaller = useTypedSelector((state) => state.preferences.isDisplayAppInstaller);
  const [microBannerLength, setMicroBannerLength] = useState(0);
  const microBannerAdjustedHeight = useMemo(() => {
    return microBannerLength > 0 ? MICRO_BANNER_HEIGHT : 0;
  }, [microBannerLength]);

  // Category bar sticky detection
  const { isSticky: isCategoryBarStuck, ref: refStickyCategoryBar } = useStickyDetection({
    margin: `-${(isDisplayAppInstaller ? APP_INSTALLER_HEIGHT : 0) + HEADER_HEIGHT + microBannerAdjustedHeight + 1}px 0px 0px 0px`,
    threshold,
  });
  const refGameList = useRef<GameListRef>(null);
  const [filterByProvider, setFilterByProvider] = useState<GameProviderModel>();
  const [filterByCategory, setFilterByCategory] = useState<GameCategoryModel>();
  const { t } = useTranslation();
  const [pageHeight, setPageHeight] = useState(window.innerHeight);

  const { scrollDir, scrollPosition } = useDetectScroll();

  // load more games when scroll to bottom
  useEffect(() => {
    if (scrollPosition.bottom < SCROLL_DOWN_OFFSET) {
      refGameList.current?.fetchNextPage();
    }
  }, [scrollDir, scrollPosition]);

  const { data } = siteApi.useGetGameRecentListQuery(
    {
      query: RECENT_GAME_QUERY_PARAMS,
    },
    {
      skip: filterByCategory?.slug !== CATEGORY_POPULAR_SLUG || userMode !== UserMode.MEMBER,
    },
  );

  const recentGames = useMemo(() => data?.entries ?? [], [data?.entries]);

  const {
    isFetchingGameCategory,
    isFetchingProviderStats,
    categoriesResponse,
    providersResponse,
    providersStatsResponse,
  } = useGameDataSource();

  const defaultCategory = useMemo(() => {
    if (!isFetchingGameCategory && !isFetchingProviderStats) {
      // Find the popular category, if not found, use recent category
      const popularCategory = categoriesResponse?.entries.find((category) => category.slug === CATEGORY_POPULAR_SLUG);
      return popularCategory ?? categoriesResponse?.entries[0];
    }
  }, [categoriesResponse, isFetchingGameCategory, isFetchingProviderStats]);

  // Reset default filter provider based on category
  const resetDefaultFilterProvider = useCallback(
    (category?: GameCategoryModel) => {
      if (!category) return;
      // If category is popular or recent, set provider to null
      if (CATEGORY_POPULAR_SLUG.includes(category?.slug)) {
        setFilterByProvider(undefined);
      } else {
        // If category is not popular or recent, set provider to default provider
        if (providersStatsResponse) {
          const standardizedStats = standardizeGameStats(providersStatsResponse);
          const defaultProvider = providersResponse?.entries
            .filter((provider) => !!standardizedStats?.[provider.id]?.[category.id])
            .find((provider) => !provider.isMaintenance);
          setFilterByProvider(defaultProvider);
        }
      }
    },
    [providersResponse?.entries, providersStatsResponse],
  );

  // Set default category if no category is selected
  useEffect(() => {
    if (!filterByCategory && defaultCategory) {
      setFilterByCategory(defaultCategory);
      resetDefaultFilterProvider(defaultCategory);
    }
  }, [defaultCategory, filterByCategory, resetDefaultFilterProvider]);

  // Set default filter provider when user mode changes
  useEffect(() => {
    if (defaultCategory) {
      setFilterByCategory(defaultCategory);
      resetDefaultFilterProvider(defaultCategory);
    }
  }, [userMode, defaultCategory, resetDefaultFilterProvider]);

  // Remove padding top & add no-scrollbar class to main element
  useLayoutEffect(() => {
    const contentWrapper = document.getElementsByTagName("main")[0] as HTMLDivElement;
    const toggleClassNames = ["!py-0"];
    if (matchPath(Paths.PRIVATE.HOME, location.pathname) && contentWrapper) {
      contentWrapper.classList.add(...toggleClassNames);
    }

    return () => {
      if (contentWrapper) {
        contentWrapper.classList.remove(...toggleClassNames);
      }
    };
  }, [location.pathname, isDisplayAppInstaller]);

  // Update page height on resize
  useEffect(() => {
    const updateDvh = () => {
      setPageHeight(window.innerHeight);
    };
    // Initial measurement
    updateDvh();
    // Update on resize
    window.addEventListener("resize", updateDvh);
    // Cleanup
    return () => window.removeEventListener("resize", updateDvh);
  }, []);

  const resetGameListScrollPosition = useCallback(() => {
    if (isCategoryBarStuck) {
      setTimeout(() => {
        window.scrollTo(0, BANNER_HEIGHT + BANNER_PADDING);
      }, 300);
    }
  }, [isCategoryBarStuck]);

  const handleOnSelectProvider = useCallback(
    (provider?: GameProviderModel) => {
      setFilterByProvider(provider);
      resetGameListScrollPosition();
    },
    [resetGameListScrollPosition],
  );

  const handleOnSelectCategory = useCallback(
    (category?: GameCategoryModel) => {
      setFilterByCategory(category);
      resetDefaultFilterProvider(category);
      resetGameListScrollPosition();
    },
    [resetGameListScrollPosition, resetDefaultFilterProvider],
  );

  const gameProviderSelectorKey = useMemo(() => {
    if (filterByCategory?.slug && !CATEGORY_POPULAR_SLUG.includes(filterByCategory.slug)) {
      return "display-provider-list";
    }
    return "hide-provider-list";
  }, [filterByCategory?.slug]);

  const isDisplayProviders = useMemo(() => {
    return !!filterByCategory?.slug && !CATEGORY_POPULAR_SLUG.includes(filterByCategory?.slug);
  }, [filterByCategory?.slug]);

  const { topOffsetProviderList, maxHeightProviderList, topOffsetStickyCategory, topOffsetMicroBanner } =
    useMemo(() => {
      const WINDOW_HEIGHT = pageHeight;
      const INSTALLER_HEIGHT = isDisplayAppInstaller ? APP_INSTALLER_HEIGHT : 0;
      return {
        topOffsetProviderList: CATEGORY_CONTAINER_HEIGHT + INSTALLER_HEIGHT + HEADER_HEIGHT + microBannerAdjustedHeight,
        maxHeightProviderList:
          WINDOW_HEIGHT - CATEGORY_CONTAINER_HEIGHT - HEADER_HEIGHT - INSTALLER_HEIGHT - microBannerAdjustedHeight,
        minHeightGameList:
          WINDOW_HEIGHT - CATEGORY_CONTAINER_HEIGHT - HEADER_HEIGHT - INSTALLER_HEIGHT - microBannerAdjustedHeight,
        topOffsetStickyCategory: INSTALLER_HEIGHT + HEADER_HEIGHT + microBannerAdjustedHeight,
        topOffsetMicroBanner: INSTALLER_HEIGHT + HEADER_HEIGHT,
      };
    }, [pageHeight, isDisplayAppInstaller, microBannerAdjustedHeight]);

  return (
    <div className="xs:scrollbar-primary relative -mx-5 px-5">
      {!!defaultCategory && <GameSearchModal preloadCategory={defaultCategory} />}

      <div className="relative animate-fade-in">
        <div
          className={cn(
            "sticky top-[49px] z-[2] -mx-5 bg-transparent bg-body bg-fixed transition-[background,padding-top]",
            {
              "pb-0.5": microBannerLength > 0,
            },
          )}
          style={{
            top: topOffsetMicroBanner,
            position: "-webkit-sticky",
          }}
        >
          <MiniPromotionBanners
            key={userMode}
            onLengthChange={setMicroBannerLength}
          />
        </div>

        <BrandBanner className="mb-[5px] mt-2" />

        {/*CATEGORY & SEARCH*/}
        <div
          className={cn(
            "sticky top-[49px] z-10 -mx-5 mb-1 flex gap-x-1.5 bg-fixed py-1 pr-5 transition-[background,padding-top]",
            {
              "bg-body pt-[5px]": isCategoryBarStuck,
            },
          )}
          ref={refStickyCategoryBar}
          style={{
            height: CATEGORY_CONTAINER_HEIGHT,
            position: "-webkit-sticky",
            top: topOffsetStickyCategory,
          }}
        >
          {/*CATEGORY*/}
          <GameCategoryList
            onSelect={handleOnSelectCategory}
            shadowClassName={cn("", {
              "!from-darkBrand-900": isCategoryBarStuck,
            })}
          />
          {/*SEARCH*/}
          <span>
            <button
              onClick={() => {
                showGameSearchModal();
              }}
              className="h-[56px] w-[64px] rounded-md bg-grdSecondary"
              aria-label="Game search entry"
            >
              <div
                className={"flex flex-col items-center justify-between gap-0.5"}
                aria-label="Game search entry"
              >
                <MdSearch
                  size={24}
                  aria-label="Game search entry"
                />
                <span
                  className="text-xs font-semibold text-content-base"
                  aria-label="Game search entry"
                >
                  {t("Search")}
                </span>
              </div>
            </button>
          </span>
        </div>

        {/*RECENT GAMES*/}
        {userMode === UserMode.MEMBER && (
          <>{filterByCategory?.slug === CATEGORY_POPULAR_SLUG && <RecentGames games={recentGames} />}</>
        )}

        {/*GAME LIST*/}
        <div className={cn("-mx-5 flex flex-grow gap-x-2")}>
          {/*PROVIDER LIST*/}
          <div
            className={cn("max-w-[85px] flex-shrink-0 basis-[85px] transition-[opacity,max-width,basis]", {
              "mr-3 !max-w-[0] basis-[0px] overflow-x-hidden opacity-0": !isDisplayProviders,
            })}
            style={{
              maxHeight: maxHeightProviderList,
            }}
          >
            <GameProviderList
              onSelect={handleOnSelectProvider}
              selected={filterByProvider}
              categoryId={filterByCategory?.id}
              key={gameProviderSelectorKey}
              className={cn("xs:scrollbar-primary w-[85px] overflow-y-auto pb-32 [direction:rtl]", {
                fixed: isCategoryBarStuck && isDisplayProviders,
              })}
              style={{
                top: topOffsetProviderList,
                maxHeight: maxHeightProviderList,
              }}
            />
          </div>

          {/*GAME GRID*/}
          <GameList
            ref={refGameList}
            filters={{
              category: filterByCategory,
              provider: filterByProvider,
            }}
            className={cn("flex-grow overflow-y-auto pb-32 pr-2", {
              "pr-5": !isDisplayProviders,
            })}
            style={{
              minHeight: maxHeightProviderList,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default HomePage;
